import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCandidate, useDeleteCandidateFile } from '@/hooks/use-candidates';
import { isConsentLocked } from '../lib/consent-actions';
import { candidateFileDownloadUrl } from '@/lib/candidates';
import { cn } from '@/lib/utils';
import type { CandidateFile } from '@/types/candidates';
import { format } from 'date-fns';
import { Cloud, Download, Eye, MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { formatFileSize, getFileIconMeta } from '../lib/file-labels';

interface CandidateFileItemProps {
  candidateId: string;
  file: CandidateFile;
  onPreview: (file: CandidateFile) => void;
}

export function CandidateFileItem({ candidateId, file, onPreview }: CandidateFileItemProps) {
  const icon = getFileIconMeta(file.mimeType, file.filename);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const del = useDeleteCandidateFile(candidateId);
  // HW-1055: nothing is removed from a candidate who revoked consent — the
  // erasure deletes it all.
  const locked = isConsentLocked(useCandidate(candidateId).data);
  const isFromPF = file.source === 'peopleforce';
  const uploader = file.uploadedBy
    ? `${file.uploadedBy.firstName} ${file.uploadedBy.lastName}`.trim() || 'Unknown'
    : isFromPF
      ? 'PeopleForce'
      : 'System';
  // The line truncates on a long uploader name, so the tooltip carries the same text.
  const metaLine = `${formatFileSize(file.sizeBytes)} · ${
    isFromPF && file.pfFetchedAt
      ? `imported from PeopleForce on ${format(new Date(file.pfFetchedAt), 'MMM d, yyyy')}`
      : `uploaded by ${uploader} on ${format(new Date(file.uploadedAt), 'MMM d, yyyy')}`
  }`;

  const handleDelete = async () => {
    await del.mutateAsync(file.id);
    setConfirmOpen(false);
  };

  return (
    <>
      <article className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2.5">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold uppercase tracking-wide',
            icon.colorClass,
          )}
        >
          {icon.ext}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-medium text-foreground" title={file.filename}>
              {file.filename}
            </div>
            <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              v{file.version}
            </span>
            {file.isLatest ? (
              <span className="shrink-0 rounded bg-hygge/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-hygge">
                Latest
              </span>
            ) : null}
            {isFromPF && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded border border-hygge/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-hygge"
                title={
                  file.pfFetchedAt
                    ? `Imported from PeopleForce on ${format(new Date(file.pfFetchedAt), 'MMM d, yyyy')}`
                    : 'Imported from PeopleForce'
                }
              >
                <Cloud className="h-2.5 w-2.5" />
                PeopleForce
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground" title={metaLine}>
            {metaLine}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="File actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {file.isPreviewable ? (
              <DropdownMenuItem onClick={() => onPreview(file)}>
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem asChild>
              <a
                href={candidateFileDownloadUrl(candidateId, file.id)}
                download={file.filename}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setConfirmOpen(true)}
              disabled={locked}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </article>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="wrap-anywhere">Delete {file.filename}?</AlertDialogTitle>
            <AlertDialogDescription>
              Version v{file.version} will be removed and disk copy deleted. Audit log entry is
              preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={del.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
